import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { ReloadOutlined } from '@ant-design/icons';
import { Modal, Button, Divider, Card, Tabs } from 'antd';
import { checkPermission } from '@/utils/permission';
import { customerServiceStatus } from '@/utils/helpers';
import { fetchOrthodonticCustomers, deleteCustomers } from '@/redux/actions/customerActions';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import DynamicTable from '@/components/DynamicTable';
import useUrlFilters from '@/hooks/useUrlFilters';
import FilterableMultiSelect from '@/components/FilterableMultiSelect';
import FilterableMultiTreeSelect from '@/components/FilterableMultiTreeSelect';
import { buildCustomerColumns } from '@/components/customers/buildCustomerColumns';
import AppointmentHistoryModal from '@/components/customers/AppointmentHistoryModal';
import { fetchFilters } from '@/redux/actions/filterActions';
import CustomerAddForm from '@/components/customers/CustomerAddForm';

const { TabPane } = Tabs;

const title = 'Danh sách khách hàng chỉnh nha';
const path = 'orthodontic-customers';
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: `/${path}`, breadcrumbName: title },
];


export default function OrthodonticCustomerList() {

    const location = useLocation();
    const dispatch = useDispatch();

    const items = useSelector(state => state.customerReducer.list_othodontic.items);
    const pagination = useSelector(state => state.customerReducer.list_othodontic.pagination);

    const doctors = useSelector(state => state.config.doctors);
    const sales = useSelector(state => state.config.sales);

    const filterData = useSelector(state => state.filterReducer.list);

    const list_provinces = filterData?.provinces;
    const customer_source_active_list = filterData?.sources;
    const branchActiveList = filterData?.branches;
    const customerStatusActiveList = filterData?.statuses;
    const service_active_list = filterData?.services;

    useEffect(() => {
        dispatch(fetchFilters());
    }, [dispatch]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpenScheduleDetail, setIsOpenScheduleDetail] = useState(false);
    const [currentData, setCurrentData] = useState(null);
    const [isOpenAddForm, setIsOpenAddForm] = useState(false);

    const routeName = 'customer';
    const allowCreate = checkPermission(routeName, 'create');
    const allowDelete = checkPermission(routeName, 'delete');

    const query = useMemo(() => {
        return qs.parse(location.search.slice(1));
    }, [location.search]);

    const loadData = useCallback((q) => {
        setIsLoading(true);
        dispatch(fetchOrthodonticCustomers(q)).finally(() => setIsLoading(false));
    }, [dispatch]);

    useEffect(() => {
        loadData(query);
    }, [query, loadData]);

    const handleRemove = () => {
        dispatch(deleteCustomers(selectedRowKeys)).then(() => {
            setSelectedRowKeys([]);
            loadData();
            setTimeout(() => Modal.destroyAll(), 800);
        });
    };

    const { handleFilterChange } = useUrlFilters({
        initialExtra: { page: 1, per_page: 30 }, // tuỳ chọn
        rangeMap: {
            // Ngày tạo
            created_date: { from: 'created_date_from', to: 'created_date_to', type: 'date' },
            // Lịch hẹn
            scheduled: { from: 'scheduled_from', to: 'scheduled_to', type: 'date' },
            // Ngày chốt
            usage_date: { from: 'usage_date_from', to: 'usage_date_to', type: 'date' },
            // Ngày sinh
            birthday: { from: 'birthday_from', to: 'birthday_to', type: 'month-day' },
        }
    });

    const openScheduleDetail = (record) => {
        setIsOpenScheduleDetail(true);
        setCurrentData(record);
    }

    const openAddForm = (isOpenAddForm) => {
        setIsOpenAddForm(isOpenAddForm)
    }

    const columns = useMemo(() => buildCustomerColumns(
        {
            onOpenScheduleDetail: (id) => openScheduleDetail(id),
            loadData: (q) => loadData(q),
        },
        {
            // ẨN/HIỆN cột:
            hiddenKeys: [
                'actions',
                'fb_name',
                'phone'
            ],

            // Ghi đè thuộc tính cột:
            overrides: {
                full_name: { width: 300 },
            }
        }
    ), [openScheduleDetail]);

    const tables = <DynamicTable
        dataSource={items || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        moduleKey="orthodontic_customer_list"
    />

    return (
        <div>
            <PageTitle routes={routes} title={title} />
            <TableActionBar
                isShowAddButton={allowCreate}
                isShowDeleteButton={allowDelete}
                onAdd={() => openAddForm(true)}
                disabled={!selectedRowKeys.length}
                onDelete={handleRemove}
                showSearch
                showFilter={true}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                filterColSpan={6}
                activeFilter={
                    query.created_date_from ||
                    query.scheduled_from ||
                    query.customer_status_id ||
                    query.selected_service_id ||
                    query.province_code ||
                    query.branch_id ||
                    query.customer_source_id ||
                    query.service_status ||
                    query.treatment_doctor_id ||
                    query.sales_staff ||
                    query.created_date
                }
                filters={
                    [
                        <FilterableMultiSelect
                            options={branchActiveList || []}
                            placeholder="Chọn cơ sở"
                            parameterName='branch_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <BaseRangeFilter
                            name="created_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày tạo', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            name="scheduled"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Lịch hẹn', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            name="usage_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày chốt', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            type="month-day"
                            name="birthday"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Sinh nhật', 'Đến ngày']}
                        />,
                        <FilterableMultiTreeSelect
                            options={service_active_list || []}
                            placeholder="Dịch vụ chốt"
                            parameterName='selected_service_id'
                        />,
                        <FilterableMultiSelect
                            options={customerServiceStatus || []}
                            placeholder="Trạng thái dịch vụ chốt"
                            parameterName='service_status'
                            optionValue="value"
                            optionLabel="label"
                        />,
                        <FilterableMultiSelect
                            options={customerStatusActiveList || []}
                            placeholder="Trạng thái khách hàng"
                            parameterName='customer_status_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={list_provinces || []}
                            placeholder="Tỉnh/Thành phố"
                            parameterName='province_code'
                            optionValue="code"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customer_source_active_list || []}
                            placeholder="Nguồn khách hàng"
                            parameterName='customer_source_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={doctors || []}
                            placeholder="Bác sĩ điều trị"
                            parameterName='treatment_doctor_id'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />,
                        <FilterableMultiSelect
                            options={sales || []}
                            placeholder="Nhân viên sales"
                            parameterName='sales_staff'
                            optionValue="user_id"
                            optionLabel="display_name"
                        />
                    ]
                }
            >
                <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData(query)}>Refresh</Button>
                {selectedRowKeys.length > 0 && <span><Divider type="vertical" />Đang chọn {selectedRowKeys.length} bản ghi </span>}
            </TableActionBar>

            <Card size='small' bodyStyle={{ padding: 0 }} className='has-custom-column-table-btn'>
                <Tabs
                    onChange={(value) => handleFilterChange('tab', value)}
                    defaultActiveKey={query.tab}
                    type="card"
                    size="small"
                    tabBarStyle={{ fontWeight: 600 }}
                >
                    <TabPane tab="Tất cả khách hàng" key="all">
                        {tables}
                    </TabPane>
                </Tabs>
            </Card>

            <CustomerAddForm
                visible={isOpenAddForm}
                onCancel={() => openAddForm(false)}
                reloadData={() => loadData()}
            />

            <AppointmentHistoryModal
                visible={isOpenScheduleDetail}
                currentData={currentData}
                onCancel={() => setIsOpenScheduleDetail(false)}
            />
        </div>
    );
}
